const forms = () => [...document.querySelectorAll('[data-predictive-search]')];

const labelsFor = (form) => {
  try {
    return JSON.parse(form.dataset.predictiveLabels || '{}');
  } catch {
    return {};
  }
};

const resultsFor = (form) => form.querySelector('[data-predictive-results]');
const inputFor = (form) => form.querySelector('input[type="search"]');
const statusFor = (form) => form.querySelector('[data-predictive-status]');
const optionsFor = (form) => [...(resultsFor(form)?.querySelectorAll('[role="option"]') || [])];

let optionSeq = 0;
const controllers = new WeakMap();
const timers = new WeakMap();
const activeIndex = new WeakMap();

const announce = (form, message) => {
  const status = statusFor(form);
  if (!status) return;
  status.textContent = '';
  window.setTimeout(() => {
    status.textContent = message;
  }, 30);
};

const setActive = (form, index) => {
  const items = optionsFor(form);
  const input = inputFor(form);
  const next = items.length ? Math.max(0, Math.min(index, items.length - 1)) : -1;
  activeIndex.set(form, next);
  items.forEach((item, itemIndex) => {
    item.setAttribute('aria-selected', itemIndex === next ? 'true' : 'false');
  });
  if (input instanceof HTMLInputElement) {
    input.setAttribute('aria-activedescendant', items[next]?.id || '');
  }
  const item = items[next];
  const panel = resultsFor(form);
  if (!item || !panel) return;
  const itemRect = item.getBoundingClientRect();
  const panelRect = panel.getBoundingClientRect();
  if (itemRect.bottom > panelRect.bottom) panel.scrollTop += itemRect.bottom - panelRect.bottom;
  if (itemRect.top < panelRect.top) panel.scrollTop -= panelRect.top - itemRect.top;
};

const closeForm = (form) => {
  const results = resultsFor(form);
  const input = inputFor(form);
  controllers.get(form)?.abort();
  window.clearTimeout(timers.get(form));
  activeIndex.set(form, -1);
  form.classList.remove('is-open');
  if (results) {
    results.hidden = true;
    results.replaceChildren();
  }
  if (input instanceof HTMLInputElement) {
    input.setAttribute('aria-expanded', 'false');
    input.setAttribute('aria-activedescendant', '');
  }
};

const imageSource = (resource) => {
  let source = '';
  if (typeof resource.image === 'string') source = resource.image;
  else if (typeof resource.featured_image?.url === 'string') source = resource.featured_image.url;
  else if (typeof resource.image?.url === 'string') source = resource.image.url;
  if (!source) return '';
  try {
    const url = new URL(source, window.location.origin);
    url.searchParams.set('width', '160');
    return url.toString();
  } catch {
    return source;
  }
};

const isZeroPrice = (value) => !value || /^[^0-9]*0(?:[.,]00)?$/.test(String(value).trim());

const formatMoney = (value) => {
  const amount = Number.parseFloat(String(value).replace(/[^0-9.]/g, ''));
  if (!Number.isFinite(amount)) return '';
  const currency = window.Shopify?.currency?.active || 'AUD';
  try {
    return new Intl.NumberFormat(document.documentElement.lang || 'en-AU', {
      style: 'currency',
      currency,
      currencyDisplay: 'narrowSymbol',
    }).format(amount);
  } catch {
    return `$${amount.toFixed(2)}`;
  }
};

const productPrice = (item, labels) => {
  const min = formatMoney(item.price_min || item.price);
  const max = formatMoney(item.price_max || item.price);
  if (!min) return '';
  if (max && min !== max) return String(labels.from || 'From __PRICE__').replace('__PRICE__', min);
  return min;
};

const optionLink = (href, title, extra = {}) => {
  const link = document.createElement('a');
  link.className = extra.viewAll ? 'header-search__view-all' : 'header-search__option';
  link.href = href;
  link.role = 'option';
  link.id = `predictive-option-${++optionSeq}`;
  link.setAttribute('aria-selected', 'false');

  if (extra.image) {
    const image = document.createElement('img');
    image.src = extra.image;
    image.alt = '';
    image.width = 64;
    image.height = 64;
    link.append(image);
  }

  const label = document.createElement('span');
  label.className = 'header-search__option-title';
  label.textContent = title;
  link.append(label);

  if (extra.price) {
    const price = document.createElement('span');
    price.className = 'header-search__option-price';
    price.append(document.createTextNode(extra.price));
    if (extra.compare) {
      const compare = document.createElement('s');
      compare.textContent = extra.compare;
      price.append(compare);
    }
    link.append(price);
  }

  return link;
};

const groupLabel = (text) => {
  const label = document.createElement('p');
  label.className = 'header-search__label';
  label.textContent = text;
  return label;
};

const searchUrl = (form, query) => {
  const url = new URL(form.action, window.location.origin);
  url.searchParams.set('q', query);
  url.searchParams.set('options[prefix]', 'last');
  return url.toString();
};

const renderResults = (form, payload, query) => {
  const results = resultsFor(form);
  const input = inputFor(form);
  const labels = labelsFor(form);
  if (!results || !(input instanceof HTMLInputElement)) return;

  const resources = payload?.resources?.results || {};
  const suggestions = resources.queries || [];
  const products = resources.products || [];
  const collections = resources.collections || [];
  const pages = resources.pages || [];
  const articles = resources.articles || [];
  const fragments = [];

  if (suggestions.length) {
    fragments.push(groupLabel(labels.suggestions || ''));
    suggestions.slice(0, 4).forEach((item) => {
      fragments.push(optionLink(item.url, item.text || item.styled_text || ''));
    });
  }

  if (products.length) {
    fragments.push(groupLabel(labels.products || ''));
    products.slice(0, 5).forEach((item) => {
      const compare = formatMoney(item.compare_at_price_min);
      const price = productPrice(item, labels);
      fragments.push(
        optionLink(item.url, item.title, {
          image: imageSource(item),
          price,
          compare: compare && !isZeroPrice(item.compare_at_price_min) && compare !== price ? compare : '',
        })
      );
    });
  }

  if (collections.length) {
    fragments.push(groupLabel(labels.collections || ''));
    collections.slice(0, 3).forEach((item) => fragments.push(optionLink(item.url, item.title)));
  }

  if (pages.length) {
    fragments.push(groupLabel(labels.pages || ''));
    pages.slice(0, 3).forEach((item) => fragments.push(optionLink(item.url, item.title)));
  }

  if (articles.length) {
    fragments.push(groupLabel(labels.articles || ''));
    articles.slice(0, 3).forEach((item) => fragments.push(optionLink(item.url, item.title, { image: imageSource(item) })));
  }

  const resultCount = fragments.filter((node) => node.getAttribute?.('role') === 'option').length;
  if (!resultCount) {
    const empty = document.createElement('p');
    empty.className = 'header-search__empty';
    empty.textContent = labels.empty || '';
    fragments.push(empty);
  }

  fragments.push(optionLink(searchUrl(form, query), labels.viewAll || '', { viewAll: true }));

  results.replaceChildren(...fragments);
  results.hidden = false;
  results.scrollTop = 0;
  form.classList.add('is-open');
  input.setAttribute('aria-expanded', 'true');
  input.setAttribute('aria-activedescendant', '');
  activeIndex.set(form, -1);

  const message =
    resultCount === 1
      ? labels.statusOne || '1 result'
      : String(labels.statusOther || '__COUNT__ results').replace('__COUNT__', String(resultCount));
  announce(form, resultCount ? message : labels.empty || '');
  document.dispatchEvent(new CustomEvent('predictive-search:open'));
};

const search = async (form, query) => {
  controllers.get(form)?.abort();
  const controller = new AbortController();
  controllers.set(form, controller);
  const labels = labelsFor(form);
  announce(form, labels.loading || '');

  const url = new URL(`${form.dataset.predictiveUrl}.json`, window.location.origin);
  url.searchParams.set('q', query);
  url.searchParams.set('resources[type]', 'product,query,collection,page,article');
  url.searchParams.set('resources[limit]', '5');
  url.searchParams.set('resources[limit_scope]', 'each');
  url.searchParams.set('resources[options][unavailable_products]', 'last');
  url.searchParams.set('resources[options][fields]', 'title,product_type,variants.title,variants.sku,vendor');

  try {
    const response = await fetch(url, {
      signal: controller.signal,
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error('Search failed');
    renderResults(form, await response.json(), query);
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') return;
    renderResults(form, {}, query);
  }
};

const onInput = (event) => {
  const input = event.target;
  if (!(input instanceof HTMLInputElement) || input.type !== 'search') return;
  const form = input.closest('[data-predictive-search]');
  if (!form) return;

  forms().forEach((other) => {
    if (other !== form) closeForm(other);
  });

  const query = input.value.trim();
  window.clearTimeout(timers.get(form));
  if (!query) {
    closeForm(form);
    return;
  }

  timers.set(
    form,
    window.setTimeout(() => {
      search(form, query);
    }, 180)
  );
};

const onKeydown = (event) => {
  const input = event.target;
  if (!(input instanceof HTMLInputElement) || !input.closest('[data-predictive-search]')) return;
  const form = input.closest('[data-predictive-search]');
  if (!form?.classList.contains('is-open')) return;

  const items = optionsFor(form);
  const current = activeIndex.get(form) ?? -1;

  if (event.key === 'ArrowDown') {
    event.preventDefault();
    setActive(form, current + 1);
  } else if (event.key === 'ArrowUp') {
    event.preventDefault();
    setActive(form, current < 0 ? items.length - 1 : current - 1);
  } else if (event.key === 'Enter' && current >= 0 && items[current] instanceof HTMLAnchorElement) {
    event.preventDefault();
    window.location.assign(items[current].href);
  }
};

const onEscape = (event) => {
  if (event.key !== 'Escape') return;
  const open = forms().filter((form) => form.classList.contains('is-open'));
  if (!open.length) return;
  event.preventDefault();
  event.stopPropagation();
  open.forEach(closeForm);
};

const onClick = (event) => {
  forms().forEach((form) => {
    if (form.classList.contains('is-open') && !form.contains(event.target)) closeForm(form);
  });
};

const bind = () => {
  if (window.__sparkyxPredictiveSearch) return;
  window.__sparkyxPredictiveSearch = true;
  document.addEventListener('input', onInput);
  document.addEventListener('keydown', onKeydown);
  document.addEventListener('keydown', onEscape, true);
  document.addEventListener('click', onClick);
};

bind();
